



















import Vue, { PropType } from 'vue'
import Address from '@/types/Address.interface'
export default Vue.extend({
    props: {
        //  The address being mapped
        address: {
            type: Object as PropType<Address>,
            required: true
        },
        //  The headline displayed in the marker info window
        headline: {
            type: String,
            default: null
        }
    },
    data() {
        return {
            error: null,
            errorMessages: {
                APPROXIMATE: 'The address did not return an exact location',
                ZERO_RESULTS: 'The address did not return any locations.'
            }
        }
    },
    async mounted() {
        //  Load the Google Maps API
        await this.loadMapsApi()

        //  If the address exists, fetch the map
        if (this.address) this.mapGet()
    },
    methods: {
        close(): void {
            this.$store.dispatch('dialogClose', 'APP')
        },
        getFilteredAddress(): string {
            //  If the address does NOT exist, return null
            if (!this.address) return null

            //  Convert the address object to a single string
            const addrString = this.$options.filters.addressString(this.address)

            //  Return the formatted address string
            return addrString
        },
        getInfoWindow(address: string): string {
            //  Create a response container
            let html = ''

            //  If a headline exists, add it to the html
            if (this.headline) html += `<div class="subtitle-1">${this.headline}</div>`

            //  Add address to the info window html
            html += `<div>${address}</div>`

            //  If an error exists, add the error message
            if (this.error)
                html += `<div style="padding:5px; padding-top:10px"><i style="color:red">${this.error}</i></div>`

            //  Return the info window
            return html
        },
        loadMapsApi() {
            return new Promise((resolve, reject) => {
                //  Check if the maps api script is already loaded
                const isLoaded = document.getElementById('googleMaps')

                //  If the script is already loaded, do nothing
                if (isLoaded) return resolve(true)

                //  Else, get our google maps API key
                const apiKey = process.env.VUE_APP_GOOGLE_MAPS_API_KEY

                //  Create a script element
                const script = document.createElement('script')

                //  Set the script src
                script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`

                //  Set the script id (so we can check for its existence later)
                script.id = 'googleMaps'

                //  Add the 'load' and 'error' handlers
                script.addEventListener('load', resolve)
                script.addEventListener('error', reject)

                //  Add script element to the DOM
                document.body.appendChild(script)
            })
        },
        mapGet(): void {
            //  Reset the error variables
            this.resetError()

            //  Create a new Google geocode client
            const geocoder = new google.maps.Geocoder()

            //  Get the address we will search for
            const address = this.getFilteredAddress()

            //  Use geocoder to find the address Lat-Long
            geocoder.geocode({ address: address }, (results, status) => {
                //  If the response status is NOT 'ok', set a error, do nothing else
                if (status !== 'OK') return this.setError(status)

                //  Else, get the location type from the results.  (Some errors are returned via 'type' property)
                const type = results[0].geometry.location_type

                //  If the type is 'ROOFTOP', we found a legit address, so remove errors
                if (type === 'ROOFTOP') this.error = null
                //  Else, set the error using the type
                else this.setError(type)

                //  Else, assemble google map options
                const mapOptions = {
                    zoom: 13,
                    mapTypeId: google.maps.MapTypeId.ROADMAP
                }

                //  Create a new google map, with options
                const map = new google.maps.Map(
                    document.getElementById('map_canvas'),
                    mapOptions
                )

                //  Update the map with geocode results
                map.setCenter(results[0].geometry.location)

                //  Get the HTML for the info window
                const info = this.getInfoWindow(address)

                //  Create an info window for the marker
                const infoWindow = new google.maps.InfoWindow({ content: info })

                //  Add a marker to the map
                const marker = new google.maps.Marker({
                    map: map,
                    position: results[0].geometry.location
                })

                //  Bind the marker click event to the infoWindow
                google.maps.event.addListener(marker, 'click', function () {
                    infoWindow.open(map, marker)
                })

                //  Pop the info window
                infoWindow.open(map, marker)
            })
        },
        resetError(): void {
            this.errorType = null
            this.hasError = false
        },
        setError(status: string): void {
            //  Get the error message for the status
            let message = this.errorMessages[status]

            //  If the message does NOT exist, set to default
            if (!message)
                message = `An unknown error occurred while locating the address. Please check the address and retry.`

            //  Set the error message
            this.error = message
        },
        wrapperStyle(): object {
            return {
                height: this.$vuetify.breakpoint.xsOnly ? '100vh' : '90vh'
            }
        }
    }
})
